import { Box, Container } from '@mui/material';
import { KnownDeliveryContainerDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Paths, ProjectPathParams } from '../../../app/router/app.router';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { LockerDetailsActionsObserver } from './locker-details-actions-observer.component';
import { useLockerDetails } from './hooks/use-locker-details.hook';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { LoadingIndicator } from '../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { makeLockerDetailsHeaderProps, makeSpaceDetailsHeaderProps } from './logic/locker-details-props.map';
import { LockerDetailsPageHeader } from '../../../ui/components/pure/page-headers/details/locker-details/locker-details-page-header.component';
import { useHasUIFeatures } from '../../../core/permissions/has-permissions.hook';
import { LockerTimelineCard } from './components/locker-timeline-card.component';
import { PermissionBoundary } from '../../../core/permissions/permission-boundary.component';

export function LockerDetailsPage({ params }: { params: ProjectPathParams<Paths.container> }) {
  const {
    container,
    locker,
    space,
    containerType,
    hub,
    hubDetails,
    tower,
    section,
    timeline,
    transactionTypes,
    actions,
    state,
    isContainerLoading,
    containerError
  } = useLockerDetails(params.deliveryContainerID);

  const hasInternalUIFeatures = useHasUIFeatures(['internal-groundfloor']);

  if (containerError) {
    if (containerError.statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }
    if (containerError.statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" entity="container" />;
    }
    if (containerError.statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" entity="container" />;
    }
    return <ErrorFullPage />;
  }

  if (isContainerLoading || hubDetails.isLoading || hub.isLoading) {
    return (
      <Container>
        <LoadingIndicator />
      </Container>
    );
  }

  if (container === undefined || hubDetails.data === undefined || hub.data === undefined) {
    return <ErrorFullPage preset="NOT_FOUND" entity="container" />;
  }

  const isLocker = containerType === KnownDeliveryContainerDtoType.Locker;

  const headerProps =
    isLocker && locker
      ? makeLockerDetailsHeaderProps(
          params.projectID,
          locker,
          hubDetails.data,
          hub.data,
          tower,
          hasInternalUIFeatures.hasAllFeatures,
          transactionTypes.data?.transactionTypes,
          actions.setAction
        )
      : space
      ? makeSpaceDetailsHeaderProps(
          params.projectID,
          space,
          hubDetails.data,
          hub.data,
          section,
          hasInternalUIFeatures.hasAllFeatures,
          transactionTypes.data?.transactionTypes,
          actions.setAction
        )
      : undefined;

  const containerLabel = isLocker ? 'Locker' : 'Space';

  return (
    <Container>
      <WindowTitle
        detail={container.name ? `${containerLabel} - ${container.name}` : 'Loading'}
        pageTitle={isLocker ? 'Lockers' : 'Spaces'}
      />
      {headerProps && <LockerDetailsPageHeader {...headerProps} />}
      <PermissionBoundary availableUiFeatures={['internal-groundfloor']}>
        <Box style={{ flexGrow: 1 }}>
          <LockerTimelineCard
            groups={timeline.groups}
            isError={timeline.isError}
            isLoading={timeline.isLoading}
            isFetchingOlder={timeline.isFetchingOlder}
            isFetchingNewer={timeline.isFetchingNewer}
            selectedDate={timeline.selectedDate}
            setSelectedDate={timeline.setSelectedDate}
            loadOlder={timeline.loadOlder}
            loadNewer={timeline.loadNewer}
            hasOlderEntries={timeline.hasOlderEntries}
            noNewerEventsFound={timeline.noNewerEventsFound}
            clearNoNewerEvents={timeline.clearNoNewerEvents}
            projectID={params.projectID}
          />
        </Box>
      </PermissionBoundary>
      <LockerDetailsActionsObserver
        actions={actions}
        containerID={container.id}
        containerType={containerType}
        state={state}
        projectID={params.projectID}
      />
    </Container>
  );
}
